import { useState } from 'react'
import { Pencil } from '@phosphor-icons/react'
import cx from 'classnames'
import { useTranslation } from 'react-i18next'
import { defaultImage, displayS3File } from '../../../utils/s3'
import { Button } from '@/components/button'

// Define type locally (previously from GraphQL)
type UpdateIncentiveItemInput = {
  id?: string
  name: string
  description?: string
  price: number
  type: string
  categoryId: string
  customerId?: string
  skuCode?: string
  digitalCode?: string
  picture?: string
}

type CardProps = {
  incentive: UpdateIncentiveItemInput
  onClick: () => void
  selectedIncentiveItems?: { id: string; skuCode?: string }[]
  onClickSelected: () => void
  isLotteryCampaign?: boolean
}

export const Card = ({
  incentive,
  onClick,
  selectedIncentiveItems,
  onClickSelected,
  isLotteryCampaign,
}: CardProps) => {
  const { t } = useTranslation()

  const [style, setStyle] = useState({
    display: 'flex',
    opacity: 0,
    padding: '0.5rem',
    margin: '0.5rem',
  })
  const { name, price, description, id, picture, skuCode } = incentive

  const selected = selectedIncentiveItems?.some((s) => s.id === id)
    ? {
      className:
        'bg-secondary !text-white hover:bg-secondary focus:ring-0 focus:ring-offset-0',
      title: 'incentives.selected',
    }
    : {
      className:
        '!text-secondary hover:text-white hover:bg-secondary focus:ring-0 focus:ring-offset-0',
      title: 'incentives.select',
    }

  return (
    <div
      key={id}
      className="flex flex-col bg-white border border-ui rounded-lg min-h-96 w-64"
    >
      <img
        className="mt-2 m-auto max-h-40 object-contain rounded-md"
        src={picture ? displayS3File(picture) : defaultImage}
      />
      <div
        className="px-2 py-1 flex flex-col h-full justify-between"
        key={`${id}`}
        style={{
          position: 'relative',
          textAlign: 'center',
        }}
        onMouseEnter={(_e) => {
          setStyle({
            display: 'flex',
            opacity: 1,
            padding: '0.5rem',
            margin: '0.5rem',
          })
        }}
        onMouseLeave={(_e) => {
          setStyle({
            display: 'flex',
            opacity: 0,
            padding: '0.5rem',
            margin: '0.5rem',
          })
        }}
      >
        <div className="w-full flex flex-row gap-4 justify-between px-2 py-1 text-start">
          <span>{name}</span>
          <span className="whitespace-nowrap">
            <span className="font-bold">€{price}</span>
            <span className="text-gray-3"> /pcs</span>
          </span>
        </div>

        <div className="w-full flex justify-between gap-2 text-start text-gray-300 text-sm p-2">
          <span className="font-medium">Code</span>
          <span className="truncate">{skuCode}</span>
        </div>

        <span className="text-gray-300 text-sm py-1">{description}</span>
        <Button
          style={style}
          size="small-icon"
          label={t('edit')}
          IconLeft={Pencil}
          iconWeight="fill"
          onClick={onClick}
          className="border-none m-auto my-0 py-1"
        />
        <button
          className={cx(
            'w-full rounded-md cursor-pointer border border-secondary !text-secondary hover:border-violet-800 hover:!text-white disabled:opacity-50 disabled:bg-primary disabled:cursor-not-allowed',
            selected.className,
          )}
          onClick={onClickSelected}
          disabled={isLotteryCampaign && !skuCode}
          title={
            isLotteryCampaign && !skuCode
              ? 'SKU code is required for lottery campaigns'
              : ''
          }
        >
          {t(selected.title)}
        </button>
      </div>
    </div>
  )
}
